import { useState, useEffect } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Calendar, TrendingUp, Users, FileText, CheckCircle, XCircle } from "lucide-react";

interface ReportData {
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  pendingAppointments: number;
  newClients: number;
  documentsProcessed: number;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

export default function Reports() {
  const [activeTab, setActiveTab] = useState("day");
  const [reportData, setReportData] = useState<ReportData>({
    totalAppointments: 0,
    completedAppointments: 0,
    cancelledAppointments: 0,
    pendingAppointments: 0,
    newClients: 0,
    documentsProcessed: 0,
  });

  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    fetchReportData(activeTab);
  }, [activeTab]);

  const fetchReportData = async (period: string) => {
    try {
      const now = new Date();
      let startDate: Date;
      let endDate = new Date(now);

      switch (period) {
        case "day":
          startDate = new Date(now.setHours(0, 0, 0, 0));
          endDate = new Date(now.setHours(23, 59, 59, 999));
          break;
        case "week":
          startDate = new Date(now.setDate(now.getDate() - now.getDay()));
          startDate.setHours(0, 0, 0, 0);
          break;
        case "month":
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case "year":
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(now.setHours(0, 0, 0, 0));
      }

      // Fetch appointments
      const { data: appointments, error: appointmentsError } = await supabase
        .from('appointments')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (appointmentsError) throw appointmentsError;

      const completed = appointments?.filter(a => a.status === 'completed').length || 0;
      const cancelled = appointments?.filter(a => a.status === 'cancelled').length || 0;
      const pending = appointments?.filter(a => a.status === 'pending' || a.status === 'confirmed').length || 0;

      // Fetch new clients
      const { count: newClientsCount, error: clientsError } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (clientsError) throw clientsError;

      // Fetch documents
      const { count: documentsCount, error: documentsError } = await supabase
        .from('client_documents')
        .select('*', { count: 'exact', head: true })
        .eq('extraction_status', 'completed')
        .gte('processed_at', startDate.toISOString())
        .lte('processed_at', endDate.toISOString());

      if (documentsError) throw documentsError;

      setReportData({
        totalAppointments: appointments?.length || 0,
        completedAppointments: completed,
        cancelledAppointments: cancelled,
        pendingAppointments: pending,
        newClients: newClientsCount || 0,
        documentsProcessed: documentsCount || 0,
      });

      // Prepare chart data
      if (period === "day") {
        const hourly = Array.from({ length: 24 }, (_, i) => ({
          hour: `${i}:00`,
          appointments: appointments?.filter(a => new Date(a.created_at).getHours() === i).length || 0
        }));
        setChartData(hourly.filter(h => h.appointments > 0));
      } else if (period === "week") {
        const daily = Array.from({ length: 7 }, (_, i) => {
          const date = new Date(startDate);
          date.setDate(startDate.getDate() + i);
          return {
            day: date.toLocaleDateString('en-US', { weekday: 'short' }),
            appointments: appointments?.filter(a => new Date(a.created_at).toDateString() === date.toDateString()).length || 0
          };
        });
        setChartData(daily);
      } else if (period === "month") {
        const weeks = Array.from({ length: 4 }, (_, i) => ({
          week: `Week ${i + 1}`,
          appointments: appointments?.filter(a => {
            const weekStart = new Date(startDate);
            weekStart.setDate(startDate.getDate() + (i * 7));
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 7);
            const apptDate = new Date(a.created_at);
            return apptDate >= weekStart && apptDate < weekEnd;
          }).length || 0
        }));
        setChartData(weeks);
      } else {
        const monthly = Array.from({ length: 12 }, (_, i) => ({
          month: new Date(startDate.getFullYear(), i, 1).toLocaleDateString('en-US', { month: 'short' }),
          appointments: appointments?.filter(a => new Date(a.created_at).getMonth() === i).length || 0
        }));
        setChartData(monthly);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    }
  };

  const pieData = [
    { name: 'Completed', value: reportData.completedAppointments },
    { name: 'Pending', value: reportData.pendingAppointments },
    { name: 'Cancelled', value: reportData.cancelledAppointments },
  ].filter(d => d.value > 0);

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight mb-2">Reports & Analytics</h2>
          <p className="text-muted-foreground">View appointment data and statistics by time period</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="day">Day</TabsTrigger>
            <TabsTrigger value="week">Week</TabsTrigger>
            <TabsTrigger value="month">Month</TabsTrigger>
            <TabsTrigger value="year">Year</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="space-y-6">
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Appointments</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{reportData.totalAppointments}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Completed</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{reportData.completedAppointments}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
                  <XCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{reportData.cancelledAppointments}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending/Confirmed</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{reportData.pendingAppointments}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">New Clients</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{reportData.newClients}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Documents Processed</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{reportData.documentsProcessed}</div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Appointments Over Time</CardTitle>
                  <CardDescription>
                    {activeTab === "day" && "Appointments by hour"}
                    {activeTab === "week" && "Appointments by day"}
                    {activeTab === "month" && "Appointments by week"}
                    {activeTab === "year" && "Appointments by month"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey={activeTab === "day" ? "hour" : activeTab === "week" ? "day" : activeTab === "month" ? "week" : "month"} />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="appointments" stroke="hsl(var(--primary))" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Appointment Status Distribution</CardTitle>
                  <CardDescription>Breakdown by status</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.value}`}
                        outerRadius={80}
                        fill="hsl(var(--primary))"
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
